const HIGHLIGHT_ATTRIBUTE = 'data-canvas-agent-highlight';

/**
 * Normalize visible control text for resilient label matching.
 * @param {*} value candidate text
 * @return {string} normalized text
 */
function normalized(value) {
	return String(value || '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Check whether a Canvas element is currently visible to the user.
 * @param {Element|null} element candidate element
 * @return {boolean} whether the element has a visible box
 */
function visible(element) {
	if (!element) return false;
	const style = window.getComputedStyle(element);
	const bounds = element.getBoundingClientRect();
	return style.display !== 'none' && style.visibility !== 'hidden' &&
		bounds.width > 0 && bounds.height > 0;
}

/**
 * Collect the accessible and visible labels of an existing control.
 * @param {Element} element Canvas control
 * @return {string} normalized combined label
 */
function labelOf(element) {
	return normalized([
		element.getAttribute('aria-label'),
		element.getAttribute('title'),
		element.getAttribute('data-tooltip'),
		element.textContent,
	].filter(Boolean).join(' '));
}

/**
 * Find the first visible existing Canvas control matching a supplied label.
 * @param {Array<string>} labels target labels
 * @return {Element|null} matching control, when present
 */
function findControl(labels) {
	const expected = labels.map(normalized);
	return [...document.querySelectorAll(
		'button, a, [role="button"], [role="menuitem"], [tabindex]',
	)].find((element) => {
		if (!visible(element)) return false;
		const actual = labelOf(element);
		return expected.some((label) =>
			actual === label || actual.startsWith(`${label} `),
		);
	}) || null;
}

/**
 * Press, or point at, the control Canvas marks with a `data-chat-action`.
 *
 * Separate from `highlightExistingAction` because that one searches by
 * visible label across `button, a, [role="button"], [role="menuitem"],
 * [tabindex]`, and several Canvas controls are a clickable `Box` - a plain
 * `div` with an `onClick`, which that selector does not reach and a label
 * search would not distinguish anyway: Firmware Management has two separate
 * "Add Firmware" controls, one for Access Points and one for Switches.
 *
 * The attribute names exactly one control, so there is nothing to
 * disambiguate and no dependence on wording that a translation would
 * change.
 *
 * @param {string} action `data-chat-action` value
 * @param {object} activation {activate, commandId}
 * @return {boolean} whether a usable control was found
 */
export function highlightChatAction(action, activation = {}) {
	const target = [...document.querySelectorAll(
		`[data-chat-action="${CSS.escape(action)}"]`,
	)].find(visible) || null;
	if (!target || unavailable(target)) return false;
	applyHighlight(target);
	const {activate, commandId} = activation;
	if (activate && commandId && !activated.has(commandId)) {
		activated.add(commandId);
		target.click();
		// Pointing at a control we just pressed ourselves reads as "click
		// this" for something already done.
		clearActionHighlight();
	}
	return true;
}

/**
 * Check the common disabled states used by existing Canvas controls.
 * @param {Element|null} element candidate control
 * @return {boolean} whether the control cannot currently be used
 */
function unavailable(element) {
	return Boolean(
		element?.disabled ||
		element?.getAttribute('aria-disabled') === 'true' ||
		element?.classList.contains('disabled'),
	);
}

/**
 * Remove the agent's mark from whatever currently carries it.
 * @return {void}
 */
export function clearActionHighlight() {
	document.querySelectorAll(`[${HIGHLIGHT_ATTRIBUTE}]`).forEach((marked) => {
		marked.style.removeProperty('outline');
		marked.style.removeProperty('outline-offset');
		marked.removeAttribute(HIGHLIGHT_ATTRIBUTE);
	});
}

/**
 * Move the agent highlight to an existing Canvas control.
 * @param {Element} element target control
 * @return {void}
 */
function applyHighlight(element, {disabled = false} = {}) {
	clearActionHighlight();
	element.setAttribute(HIGHLIGHT_ATTRIBUTE, 'true');
	element.style.setProperty('outline', '3px solid #7c3aed', 'important');
	element.style.setProperty('outline-offset', '3px', 'important');
	element.scrollIntoView({behavior: 'smooth', block: 'center'});
	// A disabled control cannot take focus, and asking it to would move focus
	// somewhere unrelated. Scrolling to it and outlining it is the whole point.
	if (!disabled) element.focus({preventScroll: true});
}

// Commands whose control has already been worked. Readiness is polled every
// 50ms for up to 12 seconds, so without this a single request would press the
// same button hundreds of times - and a download button hundreds of times is
// hundreds of files.
const activated = new Set();

/**
 * Reveal and highlight an existing Canvas control.
 *
 * The control is only ever pointed at, unless the caller says the command may
 * be carried out - which the agent allows for reading something and never for
 * changing it. An action-menu disclosure button may always be opened.
 * @param {Array<string>} actionLabels labels of the target Canvas action
 * @param {Array<string>} menuLabels optional disclosure labels
 * @param {object} activation {activate, commandId} carry-out permission
 * @return {boolean} whether an enabled target was highlighted
 */
export function highlightExistingAction(
	actionLabels,
	menuLabels = [],
	activation = {},
) {
	const target = findControl(actionLabels);
	if (target) {
		if (unavailable(target)) return false;
		applyHighlight(target);
		const {activate, commandId} = activation;
		if (activate && commandId && !activated.has(commandId)) {
			activated.add(commandId);
			target.click();
			// Pointing at a control we just pressed ourselves reads as "click
			// this" for something already done.
			clearActionHighlight();
		}
		return true;
	}
	const disclosure = findControl(menuLabels);
	if (disclosure && !unavailable(disclosure) &&
		disclosure.getAttribute('aria-expanded') !== 'true') {
		disclosure.click();
	}
	return false;
}

/**
 * Expand and highlight an existing Canvas section without recreating it.
 * @param {Array<string>} sectionLabels visible section labels
 * @return {boolean} whether the section control was found
 */
export function revealExistingSection(sectionLabels) {
	const section = findControl(sectionLabels);
	if (!section || unavailable(section)) return false;
	if (section.getAttribute('aria-expanded') === 'false') section.click();
	applyHighlight(section);
	return true;
}

/**
 * Resolve a form control from the Canvas field name the catalogue declares.
 * @param {string} name Canvas field name
 * @return {Element|null} matching control
 */
function controlByName(name) {
	if (!name) return null;
	const escaped = typeof CSS !== 'undefined' && CSS.escape
		? CSS.escape(name)
		: name.replace(/[^\w-]/g, '\\$&');
	const selectors = [
		`[name="${escaped}"]`,
		`#${escaped}`,
		`[data-field="${escaped}"]`,
		`[data-testid="${escaped}"]`,
	];
	return selectors
		.map((selector) => {
			try {
				return [...document.querySelectorAll(selector)];
			} catch (_) {
				return [];
			}
		})
		.flat()
		.find(visible) || null;
}

/**
 * Compare a visible label without the mark that says it is required.
 *
 * Canvas writes "Site Name *", "IP Address *", "Destination Site*". The
 * asterisk is punctuation about the field, not part of what it is called,
 * and a catalogue that included it would be recording the wrong name.
 * @param {*} value candidate text
 * @return {string} comparable label
 */
function normalizedLabel(value) {
	return normalized(value).replace(/\s*\*+$/, '');
}

/**
 * Resolve a form control from its visible label.
 * @param {string} text normalized field label
 * @return {Element|null} matching control
 */
function controlByLabel(text) {
	if (!text) return null;
	const label = [...document.querySelectorAll('label')].find((element) => {
		const actual = normalizedLabel(element.textContent);
		return actual === text || actual.startsWith(`${text} `);
	});
	if (!label) return null;
	const target = label.getAttribute('for')
		? document.getElementById(label.getAttribute('for'))
		: label.querySelector('input, select, textarea');
	const control = target || label;
	return visible(control) ? control : null;
}

const SECTION_ATTRIBUTE = 'data-agent-section';

/**
 * Find a section a form has named for the agent.
 *
 * Preferred over matching visible text: a form that marks its sections keeps
 * working when its wording or its markup changes, and a section that is not
 * a button - a tab, a heading - is findable at all.
 * @param {string} name declared section name
 * @return {Element|null} marked section
 */
function markedSection(name) {
	if (!name) return null;
	const escaped = typeof CSS !== 'undefined' && CSS.escape
		? CSS.escape(name)
		: name.replace(/["\\]/g, '\\$&');
	try {
		return [...document.querySelectorAll(
			`[${SECTION_ATTRIBUTE}="${escaped}"]`,
		)].find(visible) || null;
	} catch (_) {
		return null;
	}
}

/**
 * Bring a section on the way to a field into view.
 *
 * A section is made current however this form does it: a collapsed accordion
 * is expanded, an unselected tab is selected, and a plain heading needs
 * nothing done to it at all. Which of those it is comes from the element's
 * own state, so no form has to be described here.
 *
 * Separate from revealExistingSection, which also marks the section it
 * opens: here the section is only ever passed through, and marking it would
 * point at the wrong thing. Opening one changes nothing - it is the same
 * click the user would make to look inside.
 *
 * @param {string} name declared section name, as the user sees it
 * @return {boolean} whether the section was found
 */
export function expandExistingSection(name) {
	const label = String(name || '');
	const section = markedSection(label) || findControl([label]);
	if (!section || unavailable(section)) return false;
	// Only a section that says it is closed gets opened. A heading that
	// carries no state is already as open as it gets.
	if (section.getAttribute('aria-expanded') === 'false' ||
		section.getAttribute('aria-selected') === 'false') {
		section.click();
	}
	return true;
}

/**
 * Whether the page is now showing the section, by its own account.
 *
 * A heading that states no condition is always current: there is nothing
 * to open. Anything that does state one has to say it is open.
 *
 * @param {string} name declared section name, as the user sees it
 * @return {boolean} whether the section is currently showing
 */
function sectionIsCurrent(name) {
	const label = String(name || '');
	const section = markedSection(label) || findControl([label]);
	if (!section) return false;
	const selected = section.getAttribute('aria-selected');
	const expanded = section.getAttribute('aria-expanded');
	if (selected === null && expanded === null) return true;
	return selected === 'true' || expanded === 'true';
}

/**
 * Make a section current and wait until the page agrees that it is.
 *
 * expandExistingSection reports what it did, not what the page did next.
 * Two gaps followed from taking it at its word on a freshly opened page:
 * a page still mounting has no tab to find, so the one attempt hit nothing
 * and the section was reported missing; and a tab that is found records
 * its selection a tick after the click, so the step that followed ran
 * against the tab that was showing before. Retried on an interval, the
 * way sections and modals already are, so a page still arriving is waited
 * for rather than missed.
 *
 * @param {string} name declared section name, as the user sees it
 * @param {number} timeoutMs how long to keep trying
 * @param {number} intervalMs how often to try again
 * @return {Promise<void>} resolves once the section is showing
 */
export function settleExistingSection(name, timeoutMs = 5000, intervalMs = 100) {
	return new Promise((resolve, reject) => {
		let settled = false;
		let timer = null;
		const started = Date.now();
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			if (timer !== null) clearInterval(timer);
			callback();
		};
		// What this settles with: the page was looked at. Unlike a listener
		// answering an event, `sectionIsCurrent` reads the DOM and asks
		// whether the section is the one showing - so this has the strongest
		// claim of any of these paths to say it observed something. It
		// resolved with nothing, so every tab and accordion settled this way
		// acknowledged as if it had seen nothing at all.
		//
		// Only `observed`: which agent section this label stands for is the
		// caller's to say, and it adds it.
		const seen = () => resolve({observed: true});
		const attempt = () => {
			if (settled) return;
			if (sectionIsCurrent(name)) {
				finish(seen);
				return;
			}
			expandExistingSection(name);
			if (sectionIsCurrent(name)) {
				finish(seen);
				return;
			}
			if (Date.now() - started >= timeoutMs) {
				finish(() => reject(new Error('section_not_current')));
			}
		};
		attempt();
		if (!settled) timer = setInterval(attempt, intervalMs);
	});
}

/**
 * Point at the field a request was answered by, on the record now open.
 *
 * Best effort by design: the record is what was asked for, and a form that
 * does not show this field - or shows it only once a section is expanded -
 * is not a failed request. The field is named by the resource's own field
 * catalogue, so nothing here knows which fields exist.
 *
 * @param {object} field {name, label} from the field catalogue
 * @return {boolean} whether a control was highlighted
 */
export function highlightExistingField(field) {
	const control = controlByName(String(field?.name || '').trim()) ||
		controlByLabel(normalizedLabel(field?.label));
	if (!control) return false;
	// A control the form shows but will not let this record change is still
	// the thing the user asked to be shown. Refusing to point at it reported
	// the setting as missing, and sent them looking for something that was
	// on the screen in front of them.
	applyHighlight(control, {disabled: unavailable(control)});
	return true;
}

/**
 * Which of a setting's candidate places this form is actually showing.
 *
 * A form renders one of several variants of a section, and only the page
 * knows which - so the agent sends every place the setting could be and
 * which of them are really here is read off the DOM. Nothing is highlighted
 * and nothing is opened: this only reports, so the caller can ask which was
 * meant when a form turns out to show the same setting in two places at
 * once. Places sharing a trail count once - the same spot named twice is
 * not a choice anybody was offered.
 *
 * @param {Array<object>} places candidate {name, path} places
 * @return {Array<object>} the distinct places whose control is on the page
 */
export function existingFieldPlaces(places) {
	const seen = new Set();
	return (Array.isArray(places) ? places : []).filter((place) => {
		if (!place || !place.name || !controlByName(place.name)) return false;
		const trail = (place.path || []).join(' > ');
		if (seen.has(trail)) return false;
		seen.add(trail);
		return true;
	});
}

/**
 * Whether the control the agent last pointed at is one Canvas has disabled.
 * @return {boolean} true when the marked control cannot be edited
 */
export function highlightedFieldIsDisabled() {
	const marked = document.querySelector(`[${HIGHLIGHT_ATTRIBUTE}]`);
	return Boolean(marked && unavailable(marked));
}

/**
 * Highlight the first enabled control matching one of the supplied selectors.
 * @param {Array<string>} selectors scoped selectors ordered by preference
 * @return {boolean} whether an enabled control was highlighted
 */
export function highlightFirstExistingControl(selectors) {
	const control = selectors
		.map((selector) => [...document.querySelectorAll(selector)])
		.flat()
		.find((element) => visible(element) && !unavailable(element));
	if (!control) return false;
	applyHighlight(control);
	return true;
}
